import { el, fragment } from '../dom.js';
import { admin } from '../admin-api.js';
import { t, formatDate, formatMoment } from '../i18n.js';

// The page read in an emergency. The two panels below are the two failures
// that actually kill this product, and neither announces itself: a mail
// outage locks out every user because a login code is the only factor there
// is, and a stalled materialiser drains the occurrence window from the front
// with nothing turning red.
export async function renderHealth() {
  // Settled, not all: this is the page read in an emergency, so it has to be
  // at least as forgiving as the stats page it split from. With Promise.all a
  // dropped connection on either call takes the other down too — the mail and
  // occurrence cards would go blank exactly when a moderator most needs them.
  const [healthLeg, jobsLeg] = await Promise.allSettled([
    admin('GET', '/health'),
    admin('GET', '/jobs'),
  ]);

  for (const leg of [healthLeg, jobsLeg]) {
    if (leg.status === 'rejected') console.error(leg.reason);
  }

  const health = healthLeg.status === 'fulfilled' ? healthLeg.value : [];
  const jobs = jobsLeg.status === 'fulfilled' ? jobsLeg.value : [];

  return fragment([
    el('h1', { class: 'pub-title' }, t.health),
    mail(health),
    occurrences(health),
    runs(jobs),
    // Below the two that end the product, because that is the order they
    // matter in. Useful for "is this box about to hurt", not for "is the
    // thing running".
    box(health),
  ]);
}

function box(health) {
  const mb = (bytes) => t.healthBytes((Number(bytes ?? 0) / 1048576).toFixed(1));
  const job = (entry) => (entry ? `${formatMoment(entry.at)} · ${entry.status}` : t.healthNever);
  const trips = health?.rateLimit?.recent ?? [];

  return fragment([
    el('div', { class: 'card' }, [
      el('h3', { class: 'card-title' }, t.healthDatabase),
      el('p', { class: 'card-meta' },
        `${mb(health?.database?.bytes)} · ${t.healthWal} ${mb(health?.database?.walBytes)}`),
      el('p', { class: 'card-meta' }, t.healthDatabaseHint),
    ]),
    el('div', { class: 'card' }, [
      el('h3', { class: 'card-title' }, t.healthSchema),
      el('p', { class: 'card-meta' }, [
        t.healthSchemaApplied(health?.schema?.applied ?? 0),
        health?.schema?.latest ? ` · ${health.schema.latest}` : '',
      ].join('')),
    ]),
    el('div', { class: 'card' }, [
      el('h3', { class: 'card-title' }, t.healthBackup),
      el('p', { class: 'card-meta' }, job(health?.lastBackup)),
    ]),
    el('div', { class: 'card' }, [
      el('h3', { class: 'card-title' }, t.healthDrill),
      el('p', { class: 'card-meta' }, job(health?.lastRestoreDrill)),
      el('p', { class: 'card-meta' }, t.healthDrillHint),
    ]),
    el('div', { class: 'card' }, [
      el('h3', { class: 'card-title' }, t.healthRateLimit),
      trips.length
        // Grouped by the key's prefix on the server, never by the key: those
        // carry an ip_hash, and a screen listing them turns a pseudonym we
        // keep for thirty days into something somebody can read.
        ? el('ul', { class: 'card-list' }, trips.slice(0, 12).map((row) => el('li', {},
            el('p', { class: 'card-meta' },
              `${row.bucket} · ${formatMoment(row.window_start)} · ${row.hits}`))))
        : el('p', { class: 'pub-empty' }, t.healthNever),
      el('p', { class: 'card-meta' }, t.healthRateLimitHint),
    ]),
  ]);
}

function mail(health) {
  const written = health?.mail?.logWrittenAt;
  return el('div', { class: 'card' }, [
    el('h3', { class: 'card-title' }, t.healthMail),
    el('p', { class: 'card-meta' }, [
      health?.mail?.driver ?? '—',
      written ? ` · ${formatMoment(written)}` : ` · ${t.healthNever}`,
    ].join('')),
    el('p', { class: 'card-meta' }, t.healthMailHint),
  ]);
}

function occurrences(health) {
  const days = health?.occurrences?.daysAhead ?? 0;
  // Eighteen months is the specced window. Anything under a month forward means
  // the nightly job has not run in a long time, whatever the jobs table says.
  const ok = days >= 30;
  return el('div', { class: 'card' }, [
    el('div', { class: 'row' }, [
      el('h3', { class: 'card-title' }, t.healthWindow),
      el('span', { class: `badge ${ok ? 'badge-live' : 'badge-draft'} row-end` }, ok ? t.healthOk : t.healthStale),
    ]),
    el('p', { class: 'card-meta' }, [
      t.healthDays(days),
      health?.occurrences?.horizon ? ` · ${formatDate(health.occurrences.horizon)}` : '',
    ].join('')),
    el('p', { class: 'card-meta' }, t.healthWindowHint),
  ]);
}

function runs(jobs) {
  const rows = Array.isArray(jobs) ? jobs : [];
  if (!rows.length) {
    return fragment([
      el('h3', { class: 'bucket-title' }, t.healthJobs),
      el('div', { class: 'pub-empty' }, t.healthNever),
    ]);
  }

  return fragment([
    el('h3', { class: 'bucket-title' }, t.healthJobs),
    el('ul', { class: 'card-list' }, rows.map((row) => el('li', {}, el('div', { class: 'card' }, [
      el('div', { class: 'row' }, [
        el('h4', { class: 'card-title' }, row.name),
        el('span', {
          class: `badge ${row.status === 'ok' ? 'badge-live' : 'badge-draft'} row-end`,
        }, row.status),
      ]),
      el('p', { class: 'card-meta' }, [
        formatMoment(row.started_at),
        row.error ? ` · ${row.error}` : '',
      ].join('')),
    ])))),
  ]);
}
